import apiClientService from "./ApiClientService";

async function parseJsonSafe(res) {
   try {
      return await res.json();
   } catch {
      return {};
   }
}

async function throwFromResponse(res, fallbackMsg) {
   let msg = fallbackMsg;
   try {
      const err = await res.json();
      if (err?.message) {
         msg = Array.isArray(err.message) ? err.message.join(" ") : err.message;
      }
   } catch {}
   const error = new Error(msg);
   error.status = res?.status;
   throw error;
}

/** Normalizează răspunsul paginat la o formă stabilă. */
export function normalizeVideosPage(raw) {
   if (Array.isArray(raw)) {
      return { items: raw, total: raw.length, page: 1, limit: raw.length };
   }
   if (!raw || typeof raw !== "object") {
      return { items: [], total: 0, page: 1, limit: 0 };
   }

   const items =
      raw.items || raw.data || raw.results || raw.rows || raw.videos || [];
   const list = Array.isArray(items) ? items : [];

   const meta = raw.meta && typeof raw.meta === "object" ? raw.meta : {};
   const total = Number(
      raw.total ??
         meta.total ??
         raw.count ??
         raw.totalCount ??
         raw.totalItems ??
         list.length,
   );
   const page = Number(raw.page ?? meta.page ?? raw.currentPage ?? 1);
   const limit = Number(
      raw.limit ?? meta.limit ?? raw.perPage ?? raw.pageSize ?? list.length,
   );

   return {
      items: list,
      total: Number.isFinite(total) ? total : list.length,
      page: Number.isFinite(page) ? page : 1,
      limit: Number.isFinite(limit) ? limit : list.length,
   };
}

/**
 * POST /recommended-videos — creează un videoclip recomandat (Admin/Manager).
 */
export async function createRecommendedVideo({ title, youtubeUrl }) {
   const res = await apiClientService.post(`/recommended-videos`, {
      title: String(title || "").trim(),
      youtubeUrl: String(youtubeUrl || "").trim(),
   });
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut crea videoclipul recomandat.");
   }
   return parseJsonSafe(res);
}

/**
 * GET /recommended-videos — lista paginată (cu numărul de trimiteri per video).
 */
export async function getRecommendedVideos({ page = 1, limit = 50 } = {}) {
   const qs = new URLSearchParams();
   qs.set("page", String(page));
   qs.set("limit", String(limit));

   const res = await apiClientService.get(
      `/recommended-videos?${qs.toString()}`,
   );
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-au putut încărca videoclipurile.");
   }
   return normalizeVideosPage(await parseJsonSafe(res));
}

/**
 * GET /recommended-videos/pending-sends — lecții trecute fără videoclip trimis
 * (Instructor): obligațiile în așteptare.
 */
export async function getPendingVideoSends() {
   const res = await apiClientService.get(`/recommended-videos/pending-sends`);
   if (!res.ok) {
      await throwFromResponse(
         res,
         "Nu s-au putut încărca lecțiile în așteptare.",
      );
   }
   const data = await parseJsonSafe(res);
   return Array.isArray(data) ? data : normalizeVideosPage(data).items;
}

/**
 * POST /recommended-videos/send — trimite un videoclip studentului pentru o
 * lecție (Instructor). Studentul este notificat (in-app + WebSocket).
 */
export async function sendRecommendedVideo({ reservationId, videoId }) {
   const res = await apiClientService.post(`/recommended-videos/send`, {
      reservationId: Number(reservationId),
      videoId: Number(videoId),
   });
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut trimite videoclipul.");
   }
   return parseJsonSafe(res);
}

/**
 * GET /recommended-videos/{id} — detalii videoclip (cu numărul de trimiteri).
 */
export async function getRecommendedVideo(id) {
   const safe = encodeURIComponent(String(id));
   const res = await apiClientService.get(`/recommended-videos/${safe}`);
   if (!res.ok) {
      await throwFromResponse(res, "Videoclipul nu a fost găsit.");
   }
   return parseJsonSafe(res);
}

/**
 * PUT /recommended-videos/{id} — actualizează un videoclip (Admin/Manager).
 */
export async function updateRecommendedVideo(id, payload) {
   const safe = encodeURIComponent(String(id));
   const res = await apiClientService.put(`/recommended-videos/${safe}`, payload);
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut actualiza videoclipul.");
   }
   return parseJsonSafe(res);
}

/**
 * DELETE /recommended-videos/{id} — șterge un videoclip (Admin/Manager).
 */
export async function deleteRecommendedVideo(id) {
   const safe = encodeURIComponent(String(id));
   const res = await apiClientService.delete(`/recommended-videos/${safe}`);
   if (!res.ok) {
      await throwFromResponse(res, "Nu s-a putut șterge videoclipul.");
   }
   return parseJsonSafe(res);
}
